import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Divider,
  Alert,
  Fade,
  Tabs,
  Tab,
  CircularProgress,
  Slider,
} from '@mui/material';
import {
  MedicalServices as MedicalIcon,
  PlayArrow as AnalyzeIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';

// ── Environment-driven config — never hardcode in source ───────────────
const API_URL = process.env.REACT_APP_API_URL || '/api';
const API_KEY  = process.env.REACT_APP_API_KEY  || 'test-key-123';

// ── Feature definitions ────────────────────────────────────────────────
const CONTINUOUS_KEYS = new Set(['SBP', 'BMI']);

const FEATURES = [
  { key: 'CO',  label: 'Cough',                  min: 0,  max: 2,   step: 1   },
  { key: 'NS',  label: 'Night Sweats',            min: 0,  max: 2,   step: 1   },
  { key: 'BD',  label: 'Breathing Difficulty',    min: 0,  max: 2,   step: 1   },
  { key: 'FV',  label: 'Fever',                   min: 0,  max: 2,   step: 1   },
  { key: 'CP',  label: 'Chest Pain',              min: 0,  max: 2,   step: 1   },
  { key: 'SP',  label: 'Sputum',                  min: 0,  max: 2,   step: 1   },
  { key: 'IS',  label: 'Immune Suppression',      min: 0,  max: 2,   step: 1   },
  { key: 'LP',  label: 'Loss of Pleasure',        min: 0,  max: 2,   step: 1   },
  { key: 'CH',  label: 'Chills',                  min: 0,  max: 2,   step: 1   },
  { key: 'LC',  label: 'Lack of Concentration',   min: 0,  max: 2,   step: 1   },
  { key: 'IR',  label: 'Irritation',              min: 0,  max: 2,   step: 1   },
  { key: 'LA',  label: 'Loss of Appetite',        min: 0,  max: 2,   step: 1   },
  { key: 'LE',  label: 'Loss of Energy',          min: 0,  max: 2,   step: 1   },
  { key: 'LNE', label: 'Lymph Node Enlargement',  min: 0,  max: 2,   step: 1   },
  { key: 'SBP', label: 'Systolic BP (mmHg)',      min: 90, max: 180, step: 5   },
  { key: 'BMI', label: 'BMI',                     min: 15, max: 40,  step: 0.5 },
];

// ── Helpers ────────────────────────────────────────────────────────────
function getSeverityLabel(value) {
  if (value === 0) return 'Mild';
  if (value === 1) return 'Moderate';
  return 'Severe';
}

function getSliderLabel(feature, value) {
  if (CONTINUOUS_KEYS.has(feature.key)) return value;         // raw numeric for SBP, BMI
  return getSeverityLabel(value);                              // ordinal label for symptoms
}

// Clamp a value to a feature's [min, max] range and snap to its step
function clampToFeature(feature, rawValue) {
  let value = Number(rawValue);
  if (Number.isNaN(value)) return feature.min;

  // Snap to nearest step
  const steps = Math.round((value - feature.min) / feature.step);
  value = feature.min + steps * feature.step;

  // Clamp to bounds
  if (value < feature.min) value = feature.min;
  if (value > feature.max) value = feature.max;

  return value;
}

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`clinical-tabpanel-${index}`}
      aria-labelledby={`clinical-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════════════════════
const ClinicalAnalysis = () => {
  const navigate = useNavigate();

  const [selectedTab,      setSelectedTab]      = useState(0);
  const [patientId,        setPatientId]         = useState('');
  const [features,         setFeatures]          = useState(
    Object.fromEntries(FEATURES.map(f => [f.key, f.min]))
  );
  // ── Raw text-field strings, kept separate from numeric `features` state
  //    so the user can freely type ("1", "1.", "1.5") without the value
  //    snapping back mid-keystroke. Synced to `features` on blur / enter.
  const [featureInputs,    setFeatureInputs]    = useState(
    Object.fromEntries(FEATURES.map(f => [f.key, String(f.min)]))
  );
  const [isLoading,        setIsLoading]         = useState(false);
  const [progress,         setProgress]          = useState(0);
  const [result,           setResult]            = useState(null);
  const [error,            setError]             = useState(null);
  const [logs,             setLogs]              = useState([]);
  const [analysisHistory,  setAnalysisHistory]   = useState([]);

  // ── Logging (matches Acoustic / Image pattern) ──────────────────────
  const addLog = useCallback((message) => {
    setLogs(prev => [...prev, {
      timestamp: new Date().toLocaleTimeString(),
      message,
    }]);
  }, []);

  // ── Slider change — updates both numeric state and the mirrored text field
  const handleFeatureChange = useCallback((key, value) => {
    setFeatures(prev => ({ ...prev, [key]: value }));
    setFeatureInputs(prev => ({ ...prev, [key]: String(value) }));
  }, []);

  // ── Text field change — update the raw string freely while typing ───
  const handleFeatureInputChange = useCallback((key, rawValue) => {
    setFeatureInputs(prev => ({ ...prev, [key]: rawValue }));
  }, []);

  // ── Text field commit (blur or Enter) — validate, clamp, sync slider ─
  const commitFeatureInput = useCallback((feature) => {
    const raw = featureInputs[feature.key];
    const clamped = clampToFeature(feature, raw);

    setFeatures(prev => ({ ...prev, [feature.key]: clamped }));
    setFeatureInputs(prev => ({ ...prev, [feature.key]: String(clamped) }));
  }, [featureInputs]);

  const handleFeatureInputKeyDown = useCallback((e, feature) => {
    if (e.key === 'Enter') {
      commitFeatureInput(feature);
      e.target.blur();
    }
  }, [commitFeatureInput]);

  // ── Analyse ──────────────────────────────────────────────────────────
  const handleAnalyze = useCallback(async () => {
    if (!patientId) {
      setError('Please enter a Patient ID');
      return;
    }

    const featureValues = FEATURES.map(f => features[f.key]);

    setIsLoading(true);
    setError(null);
    setResult(null);
    setProgress(10);
    addLog('🚀 Starting clinical analysis...');

    try {
      setProgress(30);
      addLog('📤 Sending clinical features to model...');

      const response = await axios.post(
        `${API_URL}/v1/predict/clinical`,
        { patient_id: patientId, features: featureValues },
        {
          headers: {
            'X-CTF-API-Key': API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );

      setProgress(100);
      const data = response.data;
      setResult(data);

      setAnalysisHistory(prev => ([
        {
          id:        Date.now(),
          patientId,
          timestamp: new Date().toISOString(),
          result:    data,
        },
        ...prev,
      ].slice(0, 10)));

      addLog('✅ Analysis complete!');
      addLog(`🎯 Prediction: ${data.predicted_label || 'Unknown'} — TB prob: ${((data.tb_probability || 0) * 100).toFixed(1)}%`);

    } catch (err) {
      console.error('Clinical analysis error:', err);
      const msg = err.response?.data?.detail || err.message || 'Analysis failed';
      setError(msg);
      addLog(`❌ Error: ${msg}`);
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  }, [patientId, features, addLog]);

  // ── Reset (matches Acoustic / Image pattern) ─────────────────────────
  const resetAnalysis = useCallback(() => {
    setResult(null);
    setError(null);
    setProgress(0);
    setLogs([]);
    setPatientId('');
    setFeatures(Object.fromEntries(FEATURES.map(f => [f.key, f.min])));
    setFeatureInputs(Object.fromEntries(FEATURES.map(f => [f.key, String(f.min)])));
    addLog('🔄 Analysis reset');
  }, [addLog]);

  // ── Download JSON result (matches Acoustic pattern) ──────────────────
  const handleDownload = useCallback(() => {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href     = url;
    link.download = `clinical_result_${patientId || 'unknown'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [result, patientId]);

  // ── Feature sliders panel — slider + numeric text field per feature ──
  const renderFeatureSliders = useCallback(() => (
    <Box sx={{ maxHeight: 460, overflow: 'auto', pr: 1 }}>
      {FEATURES.map((feature) => (
        <Box key={feature.key} sx={{ mb: 2.5 }}>
          <Typography variant="body2" gutterBottom>
            {feature.label}:{' '}
            <strong>{getSliderLabel(feature, features[feature.key])}</strong>
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Slider
                value={features[feature.key]}
                min={feature.min}
                max={feature.max}
                step={feature.step}
                onChange={(_, val) => handleFeatureChange(feature.key, val)}
                valueLabelDisplay="auto"
                color={CONTINUOUS_KEYS.has(feature.key) ? 'primary' : (
                  features[feature.key] === 0 ? 'success' :
                  features[feature.key] === 1 ? 'warning' : 'error'
                )}
              />
            </Box>

            <TextField
              size="small"
              type="number"
              value={featureInputs[feature.key]}
              onChange={(e) => handleFeatureInputChange(feature.key, e.target.value)}
              onBlur={() => commitFeatureInput(feature)}
              onKeyDown={(e) => handleFeatureInputKeyDown(e, feature)}
              inputProps={{
                min: feature.min,
                max: feature.max,
                step: feature.step,
                'aria-label': `${feature.label} numeric input`,
              }}
              sx={{ width: 90 }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  ), [
    features,
    featureInputs,
    handleFeatureChange,
    handleFeatureInputChange,
    commitFeatureInput,
    handleFeatureInputKeyDown,
  ]);

  // ── Results panel (matches Acoustic / Image renderResults) ───────────
  const renderResults = useCallback(() => {
    if (!result) return null;

    const tbProb    = (result.tb_probability || 0) * 100;
    const confidence = (result.confidence    || 0) * 100;

    return (
      <Fade in={!!result} timeout={500}>
        <Box>
          <Card
            sx={{
              mb: 3,
              bgcolor: result.predicted_label === 'TB' ? 'error.light' : 'success.light',
            }}
          >
            <CardContent>
              <Typography variant="h5" gutterBottom>
                🎯 Prediction: {result.predicted_label || 'Unknown'}
              </Typography>
              <Typography variant="h6">
                TB Probability: {tbProb.toFixed(1)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={tbProb}
                sx={{ height: 12, borderRadius: 6, mt: 1 }}
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Confidence: {confidence.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Model: {result.encoder_version || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Available: {String(result.available ?? 'N/A')}
                </Typography>
                {result.checkpoint_hash && (
                  <Typography variant="body2" color="text.secondary">
                    Checkpoint: {result.checkpoint_hash.slice(0, 16)}...
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {result.clinically_valid === false && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              ⚠️ Encoder-only checkpoint loaded. This prediction is NOT clinically valid.
            </Alert>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            {result.predicted_label === 'TB'
              ? `Model detected TB with ${tbProb.toFixed(1)}% probability.`
              : `Model detected normal with ${(100 - tbProb).toFixed(1)}% confidence.`}
          </Alert>

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ mt: 2 }}
            fullWidth
          >
            Download Result JSON
          </Button>
        </Box>
      </Fade>
    );
  }, [result, handleDownload]);

  // ══════════════════════════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════════════════════════
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>

      {/* ── Header — matches Acoustic / Image exactly ───────────────── */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              🏥 Clinical Analysis Lab
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Enter clinical features for AI-powered TB risk assessment
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => navigate('/platform')}>
            Back to Platform
          </Button>
        </Box>
        <Divider sx={{ mt: 2 }} />
      </Box>

      <Grid container spacing={3}>

        {/* ── Left panel: inputs ──────────────────────────────────────── */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📋 Clinical Features
              </Typography>

              <TextField
                fullWidth
                label="Patient ID"
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
                sx={{ mb: 3 }}
              />

              {renderFeatureSliders()}

              {error && (
                <Alert
                  severity="error"
                  sx={{ mt: 2 }}
                  onClose={() => setError(null)}
                >
                  {error}
                </Alert>
              )}

              {/* ── Action buttons — Reset + Analyze matching Acoustic/Image */}
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleAnalyze}
                  disabled={isLoading || !patientId}
                  startIcon={
                    isLoading
                      ? <CircularProgress size={20} color="inherit" />
                      : <AnalyzeIcon />
                  }
                >
                  {isLoading ? 'Analyzing...' : 'Run Analysis'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetAnalysis}
                  disabled={isLoading}
                  startIcon={<RefreshIcon />}
                >
                  Reset
                </Button>
              </Box>

              {isLoading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mt: 1 }}
                  >
                    {Math.round(progress)}% Complete
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ── Right panel: Results + Logs tabs — matches Acoustic/Image ── */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={selectedTab}
                  onChange={(_, v) => setSelectedTab(v)}
                >
                  <Tab label="📊 Results" />
                  <Tab label="📝 Logs" />
                </Tabs>
              </Box>

              <TabPanel value={selectedTab} index={0}>
                {result ? renderResults() : (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 300,
                  }}>
                    <MedicalIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography color="text.secondary">
                      Enter clinical features and click{' '}
                      <strong>"Run Analysis"</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Results will appear here
                    </Typography>
                  </Box>
                )}
              </TabPanel>

              <TabPanel value={selectedTab} index={1}>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {logs.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                      sx={{ py: 4 }}
                    >
                      No logs yet. Run analysis to see the process.
                    </Typography>
                  ) : (
                    logs.map((log, idx) => (
                      <Box
                        key={idx}
                        sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          [{log.timestamp}]
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {log.message}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              </TabPanel>
            </CardContent>
          </Card>

          {/* ── Analysis history — matches Acoustic pattern ─────────── */}
          {analysisHistory.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Analysis History (Last 10)
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {analysisHistory.map((entry) => (
                    <Box
                      key={entry.id}
                      sx={{
                        py: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        {entry.patientId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Prediction: {entry.result.predicted_label || 'Unknown'}{' '}
                        | Confidence:{' '}
                        {((Number(entry.result.confidence || 0)) * 100).toFixed(2)}%{' '}
                        | TB prob:{' '}
                        {((Number(entry.result.tb_probability || 0)) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default ClinicalAnalysis;
